<template>
  <!-- «Текущий план» — персональный план, который Зоя ведёт блоком PLAN_UPDATE
       (zoya_users.zoya_plan). До 26.10.2026 нигде не показывался, и люди
       слышали «записала в план», не видя плана. Если плана ещё нет, пояснение и кнопка доступны постоянно, в том числе гостям. -->
  <div class="px-4 pt-[80px] pb-28">
    <div v-if="loading" class="flex justify-center pt-10">
      <div class="w-5 h-5 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
    </div>
    <div v-else-if="error">
      <p role="alert" class="text-13 text-red-600 mb-3">{{ error }}</p>
      <button type="button" class="plan-btn" @click="load">Попробовать снова</button>
    </div>

    <template v-else-if="plan">
      <div v-if="plan.goal || plan.strategy" class="plan-card">
        <template v-if="plan.goal">
          <p class="plan-cap">Цель</p>
          <p class="text-18 font-bold text-ink leading-snug">{{ plan.goal }}</p>
        </template>
        <template v-if="plan.strategy">
          <p class="plan-cap mt-3">Как идём</p>
          <p class="text-15 text-ink leading-snug whitespace-pre-line">{{ plan.strategy }}</p>
        </template>
      </div>

      <div v-if="plan.milestones?.length" class="plan-card">
        <p class="plan-cap">Этапы</p>
        <div v-for="(m, i) in plan.milestones" :key="i" class="flex items-start gap-2 py-1.5" :class="{ 'border-t border-line': i > 0 }">
          <span class="plan-status" :class="'is-' + (m.status || 'pending')" aria-hidden="true">
            <svg v-if="m.status === 'achieved'" width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5.2 4.3 8.5 11 1.5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-15 text-ink leading-snug">{{ m.label }}</p>
            <p class="text-12 text-ink/60">{{ STATUS[m.status] || 'впереди' }}<template v-if="m.by"> · к {{ fmt(m.by) }}</template></p>
          </div>
        </div>
      </div>

      <div v-if="plan.notes?.length" class="plan-card">
        <p class="plan-cap">Заметки Зои</p>
        <p v-for="(n, i) in plan.notes" :key="i" class="text-15 text-ink leading-snug py-1.5" :class="{ 'border-t border-line': i > 0 }">{{ n }}</p>
      </div>

      <p class="text-12 text-ink/60 mt-2 mb-4">План ведёт Зоя по вашим данным<template v-if="plan.updated_at">, обновлён {{ fmt(plan.updated_at) }}</template>.</p>
      <!-- К Зое обращаются на «ты» всегда (address_form — как Зоя обращается к человеку) -->
      <button type="button" class="plan-btn" @click="$emit('ask', 'Давай обновим мой план: ')">Обсудить план с Зоей</button>
    </template>

    <template v-else>
      <div class="plan-card mt-4">
        <h2 class="text-18 font-bold text-ink mb-2">План пока не составлен</h2>
        <p class="text-15 text-ink leading-snug mb-4">Здесь появятся цель, шаги и заметки после обсуждения с Зоей. {{ ty ? 'Расскажи' : 'Расскажите' }} ей, чего хочется достичь и что важно учесть.</p>
        <button type="button" class="plan-btn" @click="$emit('ask', 'Составь мне персональный план')">Попросить Зою составить план</button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import api from '../../services/api.js'
import { useAuthStore } from '../../store/auth.js'
import guide from '../../services/guide.js'

defineEmits(['ask'])
const authStore = useAuthStore()
const ty = computed(() => authStore.user?.address_form !== 'вы')

const STATUS = { pending: 'впереди', in_progress: 'в работе', achieved: 'сделано' }
const loading = ref(true)
const error = ref('')
const plan = ref(null)

const fmt = (iso) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get('/memory')
    plan.value = data.plan && (data.plan.goal || data.plan.strategy || data.plan.milestones?.length || data.plan.notes?.length) ? data.plan : null
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось загрузить'
  } finally {
    loading.value = false
    guide.hide('plan_empty')
  }
}

onMounted(load)
onUnmounted(() => guide.hide('plan_empty'))
defineExpose({ reload: load })
</script>

<style scoped>
.plan-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 8px;
  font-family: Inter, sans-serif;
}
.plan-cap {
  font-size: var(--fs-12);
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: var(--warm);
  margin-bottom: 4px;
}
.plan-status {
  width: 20px; height: 20px; flex: none; margin-top: 1px;
  border-radius: 50%;
  border: 1.5px solid var(--sand);
  display: flex; align-items: center; justify-content: center;
  color: #fff;
}
.plan-status.is-in_progress { border-color: #D6AC67; }
.plan-status.is-achieved { background: #D6AC67; border-color: #D6AC67; }
.plan-btn {
  width: 100%;
  min-height: 53px;
  font-family: Inter, sans-serif;
  font-size: var(--fs-16);
  font-weight: 600;
  color: var(--cream);
  background: #41291A;
  border-radius: 16px;
  padding: 14px 16px;
}
</style>
